import React, { useId } from 'react';

export type IconSource = 'material' | 'fa-solid' | 'fa-regular' | 'fa-brands';

/**
 * Renders an icon glyph from either Material Symbols or Font Awesome (both
 * loaded as web fonts in index.html), by name. Size is driven by the PARENT
 * being a CSS query container (`container-type: size`) so the glyph scales to
 * the icon block's box. Shared by BlockRenderer + PublicCampaign for parity.
 */
export const IconGlyph: React.FC<{
  source: IconSource;
  name: string;
  color?: string;
  /** Material only — filled vs outline. */
  fill?: boolean;
  /** Material only — stroke weight 100..700. */
  weight?: number;
  /** font-size; defaults to 90% of the smaller container dimension. */
  size?: string;
  /** Thin outline around the glyph. `true` → 1.5px black; pass a string for a
   *  custom `-webkit-text-stroke` value. `paint-order: stroke` keeps the stroke
   *  outside the fill so the glyph color stays intact. */
  outline?: boolean | string;
}> = ({ source, name, color, fill, weight = 500, size = '90cqmin', outline }) => {
  const strokeStyle: React.CSSProperties = outline
    ? { WebkitTextStroke: outline === true ? '1.5px #000' : outline, paintOrder: 'stroke' }
    : {};
  if (source === 'material') {
    return (
      <span
        className="material-symbols-outlined"
        aria-hidden="true"
        style={{
          fontSize: size, lineHeight: 1, color: color || 'inherit', userSelect: 'none',
          fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' ${weight}, 'GRAD' 0, 'opsz' 48`,
          ...strokeStyle,
        }}
      >
        {name || 'star'}
      </span>
    );
  }
  // Font Awesome: source is the class prefix (fa-solid / fa-regular / fa-brands).
  return (
    <i
      className={`${source} fa-${name || 'star'}`}
      aria-hidden="true"
      style={{ fontSize: size, lineHeight: 1, color: color || 'inherit', ...strokeStyle }}
    />
  );
};

/** Israeli flag as an inline SVG — used as a meter thumb icon (thumbIconSource
 *  'flag', thumbIconName 'il'). Not a font glyph, so it can't go through
 *  IconGlyph; rendered directly. Carries its own thin border (the field is
 *  white) so it stands out on the bar. `size` is the HEIGHT in px; width is the
 *  3:2 flag ratio. */
export const IsraelFlag: React.FC<{ size?: number; title?: string }> = ({ size = 28, title }) => {
  const uid = useId().replace(/[:]/g, '');
  const clip = `ilflag-${uid}`;
  const shade = `ilshade-${uid}`;
  // Fluttering banner: the cloth undulates left→right. The body outline, the two
  // blue stripes and the Magen David all ride the SAME wave so it reads as one
  // waving piece of fabric. wave(y) = a ripple curve at baseline y.
  const wave = (y: number) =>
    `M0,${y} C14,${y - 4.6} 28,${y + 4.6} 42,${y} C52,${y - 3.4} 58,${y + 3.4} 64,${y}`;
  const body =
    'M0,4 C14,-0.6 28,8.6 42,4 C52,0.6 58,7.4 64,4 L64,40 C58,43.4 52,36.6 42,40 C28,44.6 14,35.4 0,40 Z';
  return (
    <svg
      width={size * 1.45} height={size} viewBox="-2 -4 68 52"
      role="img" aria-label={title || 'דגל ישראל'} style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <clipPath id={clip}><path d={body} /></clipPath>
        <linearGradient id={shade} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#000" stopOpacity="0.10" />
          <stop offset="0.28" stopColor="#000" stopOpacity="0" />
          <stop offset="0.62" stopColor="#000" stopOpacity="0.07" />
          <stop offset="0.85" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.06" />
        </linearGradient>
      </defs>
      <path d={body} fill="#ffffff" stroke="#1f2937" strokeWidth="1.3" strokeLinejoin="round" />
      <g clipPath={`url(#${clip})`}>
        <path d={wave(11)} fill="none" stroke="#0038B8" strokeWidth="4.6" strokeLinecap="round" />
        <path d={wave(33)} fill="none" stroke="#0038B8" strokeWidth="4.6" strokeLinecap="round" />
        <g transform="translate(32,22)" fill="none" stroke="#0038B8" strokeWidth="1.8" strokeLinejoin="round">
          <polygon points="0,-8 6.9,4 -6.9,4" />
          <polygon points="0,8 6.9,-4 -6.9,-4" />
        </g>
        {/* Soft fold shading so the ripples read as 3D cloth. */}
        <rect x="-2" y="-4" width="68" height="52" fill={`url(#${shade})`} />
      </g>
    </svg>
  );
};

/** Wrapper that makes its box a query container so IconGlyph's `cqmin`
 *  font-size resolves to the block's resized dimensions. */
export const IconBox: React.FC<{
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ style, children }) => (
  <div
    style={{
      width: '100%', height: '100%', display: 'flex', alignItems: 'center',
      justifyContent: 'center', boxSizing: 'border-box', containerType: 'size',
      ...style,
    }}
  >
    {children}
  </div>
);
